import * as React from 'react';
import MUIButton from '@mui/material/Button';
import type { SxProps, Theme } from '@mui/material';

export interface ButtonProps extends React.ComponentProps<typeof MUIButton> {
  variantStyle?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link';
  sizeStyle?: 'default' | 'sm' | 'lg' | 'icon';
  sx?: SxProps<Theme>;
}

const variantStyles: Record<NonNullable<ButtonProps['variantStyle']>, SxProps<Theme>> = {
  default: {
    backgroundColor: 'primary.main',
    color: 'primary.contrastText',
    '&:hover': { backgroundColor: 'primary.dark' },
  },
  destructive: {
    backgroundColor: 'error.main',
    color: 'error.contrastText',
    '&:hover': { backgroundColor: 'error.dark' },
  },
  outline: {
    backgroundColor: 'transparent',
    color: 'text.primary',
    border: '1px solid',
    borderColor: 'divider',
    '&:hover': { backgroundColor: 'action.hover' },
  },
  secondary: {
    backgroundColor: 'secondary.main',
    color: 'secondary.contrastText',
    '&:hover': { backgroundColor: 'secondary.dark' },
  },
  ghost: {
    backgroundColor: 'transparent',
    color: 'text.primary',
    '&:hover': { backgroundColor: 'action.hover' },
  },
  link: {
    backgroundColor: 'transparent',
    color: 'primary.main',
    textDecoration: 'underline',
    '&:hover': { textDecoration: 'underline' },
  },
};

const sizeStyles: Record<NonNullable<ButtonProps['sizeStyle']>, SxProps<Theme>> = {
  default: { height: 36, px: 2, py: 1, fontSize: '0.875rem' },
  sm: { height: 32, px: 1.5, fontSize: '0.75rem' },
  lg: { height: 40, px: 3, fontSize: '1rem' },
  icon: { height: 36, width: 36, p: 0 },
};

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variantStyle = 'default', sizeStyle = 'default', sx, ...props }, ref) => {
    return (
      <MUIButton
        ref={ref}
        variant="contained"
        disableElevation
        sx={[
          {
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 500,
            borderRadius: 1,
            transition: 'all 0.2s ease-in-out',
            textTransform: 'none',
            '& svg': {
              pointerEvents: 'none',
              width: '1rem',
              height: '1rem',
              flexShrink: 0,
              marginRight: 0.5,
            },
            '&:disabled': {
              opacity: 0.5,
              pointerEvents: 'none',
            },
          },
          variantStyles[variantStyle],
          sizeStyles[sizeStyle],
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
        {...props}
      />
    );
  },
);

Button.displayName = 'Button';
